import type { ApolloClient } from '@apollo/client';
import type {
  CartCreateMutation,
  CartCreateMutationVariables,
  CartLinesAddMutation,
  CartLinesAddMutationVariables,
  CartLinesRemoveMutation,
  CartLinesRemoveMutationVariables,
  CartLinesUpdateMutation,
  CartLinesUpdateMutationVariables,
  CartQuery,
  CartQueryVariables,
} from '@/types/graphql/storefront';
import CartLinesAdd from './graphql/addToCart.gql';
import Cart from './graphql/cart.gql';
import CartCreate from './graphql/createCart.gql';
import CartLinesRemove from './graphql/removeFromCart.gql';
import CartLinesUpdate from './graphql/updateCartItem.gql';
import type { CartItemInput, Cart as CartType } from './storefront.types';
import createStorefrontApolloClient from './storefront-apollo-client';

const storefrontClient = createStorefrontApolloClient();

const handleData = <TData>(
  gqlResponse:
    | ApolloClient.MutateResult<TData>
    | ApolloClient.QueryResult<TData>
): TData => {
  if (gqlResponse.data != null) {
    return gqlResponse.data;
  } else {
    if (gqlResponse.error != null) {
      const error = gqlResponse.error;
      console.error('Shopify Storefront API Exceptions:', error.message);
      throw new Error(`Storefront API returned exceptions: ${error.message}`);
    } else {
      console.error(
        'Shopify Storefront, unknown error no data or error object'
      );
      throw new Error('Storefront API returned unknown error');
    }
  }
};

export async function createCart(
  items: CartItemInput[]
): Promise<CartType | undefined | null> {
  const variables: CartCreateMutationVariables = {
    input: {
      lines: items.map(({ merchandiseId, quantity }) => ({
        merchandiseId,
        quantity,
      })),
    },
  };

  const response = await storefrontClient.mutate<
    CartCreateMutation,
    CartCreateMutationVariables
  >({
    mutation: CartCreate,
    variables,
  });

  const parsedResponse = handleData<CartCreateMutation>(response);
  return parsedResponse?.cartCreate?.cart as CartType;
}

export async function addToCart(
  cartId: string,
  items: CartItemInput[]
): Promise<CartType | undefined | null> {
  const variables = { cartId, lines: items };

  const response = await storefrontClient.mutate<
    CartLinesAddMutation,
    CartLinesAddMutationVariables
  >({
    mutation: CartLinesAdd,
    variables,
  });

  const parsedResponse = handleData(response);
  return parsedResponse?.cartLinesAdd?.cart as CartType;
}

export async function removeFromCart(
  cartId: string,
  lineItemIds: string[]
): Promise<CartType | undefined | null> {
  const variables: CartLinesRemoveMutationVariables = {
    cartId,
    lineIds: lineItemIds,
  };

  const response = await storefrontClient.mutate<
    CartLinesRemoveMutation,
    CartLinesRemoveMutationVariables
  >({
    mutation: CartLinesRemove,
    variables,
  });

  const parsedResponse = handleData(response);
  return parsedResponse?.cartLinesRemove?.cart as CartType;
}

export async function updateCartLines(
  cartId: string,
  updates: { id: string; quantity: number }[]
): Promise<CartType | undefined | null> {
  const variables = {
    cartId,
    lines: updates,
  };
  const response = await storefrontClient.mutate<
    CartLinesUpdateMutation,
    CartLinesUpdateMutationVariables
  >({
    mutation: CartLinesUpdate,
    variables,
  });

  const parsedResponse = handleData(response);
  return parsedResponse?.cartLinesUpdate?.cart as CartType;
}

export async function getCart(
  cartId: string
): Promise<CartType | undefined | null> {
  const variables: CartQueryVariables = { cartId };
  const response = await storefrontClient.query<CartQuery, CartQueryVariables>({
    query: Cart,
    variables,
  });

  const parsedResponse = handleData(response);
  return parsedResponse?.cart as CartType;
}
